import { Commercials } from "@/pages/deals/types";
import { APILookupValue, APIObject } from "@/services/api/types";
import { IcpFit, IcpMetadata } from "@/entities/icpFit/types";
import { ISOStringFormat } from "date-fns";

export const OTHER_COMPANY_ID = "c-other";

// Sentinel IDs are FK integrity placeholders, not real companies. They must
// never be opened in an editable detail view or sent to mutating endpoints
// (CRM-6081).
export const RESERVED_COMPANY_IDS: readonly string[] = [OTHER_COMPANY_ID];

export const isReservedCompanyId = (id: string | null | undefined): boolean => {
  if (id == null) return false;
  return RESERVED_COMPANY_IDS.includes(id);
};

export enum CompanyColumn {
  NAME = "name",
  STATUS = "status",
  PARENT_COMPANY = "parentCompany",
  WEBSITE = "website",
  SDR_OWNER = "sdrOwner",
  AE_OWNER = "aeOwner",
  CSM_OWNER = "csmOwner",
  INDUSTRY_NAME = "industryName",
  SUB_INDUSTRY_NAME = "subIndustryName",
  SEGMENT = "segment",
  CITY = "city",
  STATE_REGION = "stateRegion",
  POSTAL_CODE = "postalCode",
  ADDRESS = "address",
  COUNTRY = "country",
  PHONE = "phone",
  NUM_OF_EMPLOYEES = "numOfEmployees",
  CREATED_AT = "createdAt",
  STATUS_CHANGED_AT = "statusChangedAt",
  ARR = "arr",
  MRR = "mrr",
  ANNUAL_CONTRACT_VALUE = "annualContractValue",
  TOTAL_CONTRACT_VALUE = "totalContractValue",
  CLOSEST_CONTRACT_END_DATE = "closestContractEndDate"
}

export enum CompanyStatus {
  PROSPECT = 1,
  ACTIVE_CUSTOMER,
  CHURNED,
  ON_HOLD_FOR_PROSPECTING,
  DISQUALIFIED
}

export interface CompanyIndustriesResponse {
  industries: APILookupValue[];
}

export interface CompanySegmentsResponse {
  segments: APILookupValue[];
}

export type CompanyCommercialsResponse = Commercials[];

interface CompanyData extends APIObject {
  name: string;
  status: CompanyStatus;
  website: string | null;
  sdrOwner: string | null;
  aeOwner: string | null;
  csmOwner: string | null;
  industryId: number | null;
  industryName: string | null;
  subIndustryId: number | null;
  subIndustryName: string | null;
  city: string | null;
  stateRegion: string | null;
  postalCode: string | null;
  address: string | null;
  country: string | null;
  phone: string | null;
  numOfEmployees: number | null;
  createdAt: ISOStringFormat | null;
  statusChangedAt?: ISOStringFormat | null;
  parentCompany: string | null;
  parentCompanyName?: string | null;
  icpFit?: IcpFit | null;
  icpMetadata?: IcpMetadata | null;
}

interface CompaniesPage {
  companies: CompanyData[];
  total: number;
  page: number;
  size: number;
  pages: number;
}

export type { CompanyData, CompaniesPage };
